import { QuickLookupSchema } from "@api/generated/types.gen";

import { useQuickLookups } from "@hooks/queries/use.cosmos";

type QuickLookupsProps = {
  onSelect: (lookup: QuickLookupSchema) => void;
};

/**
 * The Quick Lookups list: fixed questions from the cosmos-service register.
 * Picking one asks that question as-is — there is no free text within a Quick
 * Lookup; typing goes to Document Search. Renders nothing if the register
 * can't be loaded, so Document Search still works on its own.
 */
export const QuickLookups = ({ onSelect }: QuickLookupsProps) => {
  const { data, isLoading, isError } = useQuickLookups(true);

  if (isError || (!isLoading && !data?.lookups?.length)) return null;

  return (
    <div>
      <h3 className="mb-2 font-head text-xs font-medium text-foreground-muted">
        Quick Lookups
      </h3>
      <div className="flex flex-wrap gap-1.5">
        {isLoading
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                className="h-7 w-28 animate-pulse rounded-full bg-gray-100"
              />
            ))
          : data?.lookups?.map((lookup) => (
              <button
                key={lookup.id}
                type="button"
                title={lookup.description}
                // A mouse pick keeps focus on the search input; a keyboard user
                // tabs here, and the container's blur check keeps the dropdown
                // open. The click (mouse or keyboard) does the selecting.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => onSelect(lookup)}
                className="rounded-full bg-primary-50 px-3 py-1.5 text-xs font-normal text-primary transition-colors hover:bg-primary-100"
              >
                {lookup.label}
              </button>
            ))}
      </div>
      {data?.boundary && (
        <p className="mt-2 text-xs text-foreground-muted">{data.boundary}</p>
      )}
    </div>
  );
};
